/**
 * React hook for subscribing to EventFrame streams from the gateway.
 *
 * Usage:
 *   useEventStream("chat", (payload) => { ... });
 *   useEventStream("task", (payload) => { ... });
 */

import { useEffect, useRef } from "react";

type EventHandler = (payload: unknown) => void;

// Central event bus — gateway-client dispatches here, hooks subscribe
const listeners = new Map<string, Set<EventHandler>>();

/** Subscribe a handler to a specific event type. Returns unsubscribe function. */
export function subscribeEvent(event: string, handler: EventHandler): () => void {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event)!.add(handler);
  return () => {
    listeners.get(event)?.delete(handler);
  };
}

/** Dispatch an event to all subscribers. Called by the gateway client's onEvent. */
export function dispatchEvent(event: string, payload: unknown): void {
  const handlers = listeners.get(event);
  if (handlers) {
    for (const handler of handlers) {
      try {
        handler(payload);
      } catch {
        // Don't let one handler break others
      }
    }
  }
}

/**
 * React hook to subscribe to gateway EventFrame streams.
 *
 * Always subscribes — events dispatched while disconnected are simply
 * not received (the gateway doesn't send them). No connection-state gating
 * needed; this avoids subscribe/unsubscribe churn on reconnects.
 *
 * @param event - Event type to listen for ("chat", "agent", "task", "session.tool")
 * @param handler - Callback invoked with event payload
 */
export function useEventStream(event: string, handler: EventHandler): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const unsubscribe = subscribeEvent(event, (payload) => {
      handlerRef.current(payload);
    });
    return unsubscribe;
  }, [event]);
}

/**
 * The namespace the runtime plugin must use for its events to be DELIVERED.
 *
 * Pristine OpenClaw core drops any gateway event that has no entry in its
 * module-private `EVENT_SCOPE_GUARDS` table and is not in the `plugin.`
 * namespace — silently, for every client. So every lexiun domain event
 * (`case.updated`, `mission.progress`, `tasklist.changed`, …) travels the wire
 * as `plugin.<name>`. Core's own events (`session.tool`, `chat`, `task`, …) are
 * guarded and keep their bare names — use plain `useEventStream` for those.
 *
 * Mirrors `lexEventName()` in
 * `lexruntime/openclaw/extensions/lexiun-core/src/gateway-methods/broadcast.ts`.
 * The two halves must agree; see `ai-docs/lexiun-gateway-broadcast-fix-plan.md`.
 */
const LEX_EVENT_NAMESPACE = "plugin.";

/** Wire name for a bare lexiun domain event. Idempotent. */
export function lexEventName(event: string): string {
  return event.startsWith(LEX_EVENT_NAMESPACE) ? event : `${LEX_EVENT_NAMESPACE}${event}`;
}

/**
 * Subscribe to a lexiun DOMAIN event by its bare name (`"case.updated"`),
 * resolving the `plugin.` wire namespace for you.
 *
 * Use this for anything the lexiun-core plugin emits. For core's own guarded
 * events (`session.tool`, `chat`, `task`) use `useEventStream` directly.
 */
export function useLexEventStream(event: string, handler: EventHandler): void {
  useEventStream(lexEventName(event), handler);
}

// DEV-only test accessor: lets Playwright inject synthetic events
// without round-tripping through the real gateway. Used by L.6.2 to
// simulate `session.tool` broadcasts for the `lx_open_file` signal.
declare global {
  interface Window {
    __eventBus?: {
      dispatch: typeof dispatchEvent;
      subscribe: typeof subscribeEvent;
    };
  }
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__eventBus", {
    value: { dispatch: dispatchEvent, subscribe: subscribeEvent },
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
